import { useState, useEffect, useRef } from "react";
import React from "react";
import { Filter, X, CheckCheck, Trash2, ChevronDown, Pencil, ArrowUpRight, Check, RotateCcw, Calendar } from "lucide-react";
import { funcCatOptions } from "../../components/MyTask/data";
import axiosInstance from "../../API/interceptor";

// ── Shared constants ──────────────────────────────────────────────────────────

export const TABS = ["Todo List", "Circles"];

export const PRIORITY_COLORS = {
  High:   { bg: "#7C3AED0f", border: "#7C3AED28", text: "#7C3AED" },
  Medium: { bg: "#D974060f", border: "#D9740628", text: "#D97406" },
  Low:    { bg: "#0596690f", border: "#05966928", text: "#059669" },
};

export const STATUS_COLORS = {
  pending:       { bg: "#FFF7ED",    border: "#FDE68A",   text: "#D97406" },
  open:          { bg: "#FFF7ED",    border: "#FDE68A",   text: "#D97406" },
  "in-progress": { bg: "#7C3AED0f", border: "#7C3AED28", text: "#7C3AED" },
  done:          { bg: "#0596690f", border: "#05966928", text: "#059669" },
  complete:      { bg: "#0596690f", border: "#05966928", text: "#059669" },
};

// ── shared style fragments ────────────────────────────────────────────────────
const HEADER_GRADIENT   = "bg-[linear-gradient(135deg,#7C3AED,#6D28D9)]";
const HEADER_GRADIENT_3 = "bg-[linear-gradient(135deg,#7C3AED_0%,#6D28D9_60%,#4F46E5_100%)]";
const OUTLINE_BTN_VIOLET = "bg-white border-[1.5px] border-violet-600 text-violet-600";
const FIELD_BASE = "border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 outline-none bg-white transition-all shadow-[0_1px_4px_rgba(108,95,252,0.06)] focus:border-violet-600 focus:shadow-[0_0_0_3px_rgba(124,58,237,0.1)]";

// ── Shared UI components ──────────────────────────────────────────────────────

export function Badge({ label, colors }) {
  const display = label === "complete" ? "Completed" : label === "open" ? "Open" : label;
  return (
    <span className="text-[10px] md:text-xs font-semibold px-2 py-0.5 rounded-md capitalize"
      style={{ background: colors.bg, border: `1px solid ${colors.border}`, color: colors.text }}>
      {display}
    </span>
  );
}

export function ActionBtn({ icon, label, color, bg, border, hoverBg, onClick }) {
  const [show, setShow] = React.useState(false);
  return (
    <div className="relative flex items-center justify-center"
      onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <button onClick={onClick}
        className="w-7 h-7 flex items-center justify-center rounded-lg border transition-colors cursor-pointer"
        style={{ background: bg, color, borderColor: border }}
        onMouseEnter={e => e.currentTarget.style.background = hoverBg}
        onMouseLeave={e => e.currentTarget.style.background = bg}>
        {icon}
      </button>
      {show && (
        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-white px-2 py-1 rounded-lg pointer-events-none z-50 bg-[#1e1b4b] shadow-[0_4px_12px_rgba(0,0,0,0.2)]">
          {label}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-[#1e1b4b]" />
        </div>
      )}
    </div>
  );
}

// ── TodoFilterPanel — original right-side offcanvas design ───────────────────

export function TodoFilterPanel({ open, onClose, onApply, onClear }) {
  const dateCls = "w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-50 transition-all";
  const emptyF  = { dateFrom: "", dateTo: "", funcCat: "" };
  const [f, setF] = useState(emptyF);

  const handleClear = () => { setF(emptyF); onClear(); onClose(); };

  const DateField = ({ label, k }) => (
    <div className="mb-3.5">
      <label className="block text-xs font-bold text-gray-700 mb-1.5">{label}</label>
      <div className="relative">
        <div className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${HEADER_GRADIENT}`}>
          <Calendar size={11} color="#fff" />
        </div>
        <input type="date" value={f[k]} onChange={e => setF(p => ({ ...p, [k]: e.target.value }))} className={dateCls} />
      </div>
    </div>
  );

  return (
    <>
      {open && <div onClick={onClose} className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[1100]" />}
      <div className={`fixed top-0 right-0 bottom-0 w-[360px] max-w-[95vw] bg-white z-[1101] shadow-[-8px_0_40px_rgba(0,0,0,.13)] flex flex-col transition-transform duration-300 ease-[cubic-bezier(.4,0,.2,1)] ${
        open ? "translate-x-0" : "translate-x-full"
      }`}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${HEADER_GRADIENT}`}>
              <Filter size={13} color="#fff" />
            </div>
            <span className="text-base font-extrabold text-slate-800">Filter</span>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 rounded-lg border border-gray-200 bg-white flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors">
            <X size={14} className="text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <DateField label="Date From" k="dateFrom" />
          <DateField label="Date To"   k="dateTo" />

          <div className="mb-3.5">
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Functional Categories</label>
            <div className="relative">
              <select value={f.funcCat} onChange={e => setF(p => ({ ...p, funcCat: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none bg-white focus:border-violet-400 focus:ring-2 focus:ring-violet-50 transition-all appearance-none">
                <option value="">All Categories</option>
                {funcCatOptions.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
              </select>
              <ChevronDown size={13} className="text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-gray-100 bg-slate-50 flex gap-2.5 flex-shrink-0">
          <button onClick={handleClear}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors ${OUTLINE_BTN_VIOLET}`}>
            Clear
          </button>
          <button onClick={() => { onApply(f); onClose(); }}
            className={`flex-1 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer ${HEADER_GRADIENT} shadow-[0_3px_12px_rgba(109,40,217,.35)]`}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}

// ── DeleteConfirmOffcanvas ────────────────────────────────────────────────────

function DeleteConfirmModal({ onClose, onConfirm }) {
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[1200]" />
      <div className="fixed top-0 right-0 bottom-0 w-[360px] max-w-[95vw] bg-white z-[1201] shadow-[-8px_0_40px_rgba(0,0,0,.13)] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0 bg-[linear-gradient(135deg,#ef4444,#dc2626)]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/20">
              <Trash2 size={13} color="#fff" />
            </div>
            <span className="text-base font-extrabold text-white">Delete Task</span>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer border-none bg-white/15 hover:bg-white/20">
            <X size={14} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 gap-5">
          <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-50 border-2 border-red-200">
            <Trash2 size={34} color="#ef4444" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-bold text-slate-800 m-0">Are you sure?</h2>
            <p className="text-sm text-slate-500 mt-2 m-0 leading-relaxed">
              This task will be permanently deleted.<br/>This action cannot be undone.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-slate-50 flex gap-2.5 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors bg-white border-[1.5px] border-slate-200 text-slate-500">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer hover:opacity-90 bg-[linear-gradient(135deg,#ef4444,#dc2626)] shadow-[0_3px_12px_rgba(239,68,68,.35)]">
            Yes, Delete it
          </button>
        </div>
      </div>
    </>
  );
}

// ── AddTodoModal — right offcanvas ───────────────────────────────────────────

export function AddTodoModal({ onClose, onAdd }) {
  const [taskName,    setTaskName]    = useState("");
  const [taskDetails, setTaskDetails] = useState("");
  const [category,    setCategory]    = useState([]);
  const [dropOpen,    setDropOpen]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const dropRef    = React.useRef();

  React.useEffect(() => {
    const fn = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const toggleCat = (label) => setCategory(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]);
  const removeCat = (label, e) => { e.stopPropagation(); setCategory(prev => prev.filter(x => x !== label)); };

  const handleCreate = async () => {
    if (!taskName.trim()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("task", taskName.trim());
      fd.append("task_details", taskDetails.trim());
      category.forEach(c => fd.append("functional_categories", c));
      const res = await axiosInstance.post("/task_assignment/todo_list/add", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (res.data?.success) {
        onAdd({ taskName: taskName.trim(), details: taskDetails.trim(), category, task_id: res.data.task_id });
        onClose();
      }
    } catch { /* silent */ }
    finally { setSaving(false); }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-[1200]" />
      <div className="fixed top-0 right-0 bottom-0 w-[420px] max-w-[95vw] bg-white z-[1201] shadow-[-8px_0_40px_rgba(0,0,0,.13)] flex flex-col">

        {/* Header */}
        <div className={`relative flex items-center justify-between px-5 py-4 overflow-hidden flex-shrink-0 ${HEADER_GRADIENT_3}`}>
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full opacity-20 bg-white/30" />
          <div className="absolute bottom-0 left-8 w-14 h-14 rounded-full opacity-10 bg-white/50" />
          <div className="relative z-10">
            <h2 className="text-base font-bold text-white m-0 leading-tight">Add New Task</h2>
            <p className="text-[11px] text-violet-200 m-0 mt-0.5">Fill in the details to create a new todo</p>
          </div>
          <button onClick={onClose}
            className="relative z-10 w-8 h-8 rounded-lg flex items-center justify-center border-none cursor-pointer bg-white/10 hover:bg-white/20">
            <X size={16} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5 bg-[#fafbff]">

          {/* Task Name */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Task Name</label>
            <input autoFocus value={taskName} onChange={e => setTaskName(e.target.value)}
              placeholder="e.g. Design homepage wireframe..."
              onKeyDown={e => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") onClose(); }}
              className={FIELD_BASE} />
          </div>

          {/* Task Details */}
          <div className="flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Task Details</label>
            <textarea value={taskDetails} onChange={e => setTaskDetails(e.target.value)}
              placeholder="Add more context or details about this task..."
              rows={4}
              className={`${FIELD_BASE} resize-none`} />
          </div>

          {/* Functional Category */}
          <div className="flex flex-col gap-2" ref={dropRef}>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">Functional Category</label>
            <button onClick={() => setDropOpen(o => !o)}
              className={`w-full flex items-center justify-between border rounded-xl px-4 py-3 text-sm bg-white cursor-pointer outline-none transition-all min-h-[48px] ${
                dropOpen
                  ? "border-violet-600 shadow-[0_0_0_3px_rgba(124,58,237,0.1)]"
                  : "border-gray-200 shadow-[0_1px_4px_rgba(108,95,252,0.06)]"
              }`}>
              <span className="flex flex-wrap gap-1 flex-1">
                {category.length > 0
                  ? category.map(c => { const o = funcCatOptions.find(x => x.label === c); return (
                      <span key={c} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md"
                        style={{ background: o?.bg, color: o?.color, border: `1px solid ${o?.border}` }}>
                        {c}<span onClick={e => removeCat(c, e)} className="cursor-pointer opacity-60 hover:opacity-100">×</span>
                      </span>); })
                  : <span className="text-slate-400">Select categories...</span>}
              </span>
              <ChevronDown size={15} className={`text-slate-400 transition-transform flex-shrink-0 ${dropOpen ? "rotate-180" : "rotate-0"}`} />
            </button>
            {dropOpen && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-y-auto mt-1 max-h-[240px]">
                {funcCatOptions.map((o, i) => {
                  const checked = category.includes(o.label);
                  return (
                    <div key={o.label} onClick={() => toggleCat(o.label)}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${i < funcCatOptions.length - 1 ? "border-b border-gray-50" : ""} ${checked ? "" : "hover:bg-[#f8f7ff]"}`}
                      style={checked ? { background: o.bg } : undefined}>
                      <div className={`w-4 h-4 rounded flex-shrink-0 flex items-center justify-center border-2 ${checked ? "bg-violet-600 border-violet-600" : "border-gray-300"}`}>
                        {checked && <Check size={9} color="#fff" strokeWidth={3} />}
                      </div>
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: o.color }} />
                      <span className={`text-sm font-medium flex-1 ${checked ? "" : "text-gray-700"}`} style={checked ? { color: o.color } : undefined}>{o.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-gray-100 bg-slate-50 flex gap-2.5 flex-shrink-0">
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer ${OUTLINE_BTN_VIOLET}`}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={!taskName.trim() || saving}
            className="flex-1 py-2.5 rounded-xl border-none text-white text-sm font-bold cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed bg-[linear-gradient(135deg,#10B981,#059669)] shadow-[0_3px_12px_rgba(16,185,129,.35)]">
            {saving ? "Creating..." : "Create Task"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── TodoListTab — original design + API calls ─────────────────────────────────

export default function TodoListTab({ search, selected, setSelected, todos, setTodos, setConvertDraft, activeFilters }) {

  const isDone = t => t.status === "done" || t.status === "complete";

  const filtered = todos.filter(t => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (activeFilters.funcCat && !(t.group || []).includes(activeFilters.funcCat)) return false;
    if (activeFilters.dateFrom && t.created < activeFilters.dateFrom) return false;
    if (activeFilters.dateTo   && t.created > activeFilters.dateTo)   return false;
    return true;
  });

  const filteredIds = filtered.map(t => t.id);
  const allChecked  = filteredIds.length > 0 && filteredIds.every(id => selected.includes(id));

  const toggleAll = () =>
    setSelected(allChecked ? selected.filter(id => !filteredIds.includes(id)) : [...new Set([...selected, ...filteredIds])]);
  const toggleOne = id =>
    setSelected(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id]);

  const [deleteId,     setDeleteId]     = React.useState(null);
  const [editId,       setEditId]       = React.useState(null);
  const [editTitle,    setEditTitle]    = React.useState("");
  const [editDetails,  setEditDetails]  = React.useState("");
  const [editGroup,    setEditGroup]    = React.useState([]);
  const [editDropOpen, setEditDropOpen] = React.useState(false);
  const editDropRef = React.useRef();

  const startEdit  = (t) => { setEditId(t.id); setEditTitle(t.title); setEditDetails(t.details || ""); setEditGroup(t.group || []); setEditDropOpen(false); };
  const cancelEdit = () => setEditId(null);

  /* ── Save edit → API ── */
  const saveEdit = async () => {
    if (!editTitle.trim()) return;
    const fd = new FormData();
    fd.append("task_name", editTitle.trim());
    fd.append("task_details", editDetails.trim());
    editGroup.forEach(c => fd.append("functional_categories", c));
    try {
      await axiosInstance.post(`/update/${editId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTodos(prev => prev.map(t => t.id === editId ? { ...t, title: editTitle.trim(), details: editDetails.trim(), group: editGroup } : t));
    } catch { /* silent */ }
    setEditId(null);
  };

  /* ── Complete → API ── */
  const handleComplete = async (taskId) => {
    try {
      await axiosInstance.post(`/complete/${taskId}`);
      setTodos(prev => prev.map(t => t.id === taskId ? { ...t, status: "complete" } : t));
    } catch { /* silent */ }
  };

  /* ── Undo → API ── */
  const handleUndo = async (taskId) => {
    const fd = new FormData();
    fd.append("status", "open");
    try {
      await axiosInstance.post(`/update/${taskId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTodos(prev => prev.map(t => t.id === taskId ? { ...t, status: "open" } : t));
    } catch { /* silent */ }
  };

  /* ── Delete → API ── */
  const handleDelete = async () => {
    try {
      await axiosInstance.post(`/delete/${deleteId}`);
      setTodos(prev => prev.filter(t => t.id !== deleteId));
      setSelected(prev => prev.filter(id => id !== deleteId));
    } catch { /* silent */ }
    setDeleteId(null);
  };

  return (
    <div className="p-4">
      {deleteId && <DeleteConfirmModal onClose={() => setDeleteId(null)} onConfirm={handleDelete} />}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-slate-50">
              <th className="w-10 px-4 py-3 text-center">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} className="cursor-pointer accent-violet-600 block mx-auto" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Task Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Task Details</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Functional Categories</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Created</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.id}
                className={`border-b border-gray-50 transition-colors ${
                  editId === t.id    ? "bg-violet-50"
                  : isDone(t)        ? "bg-gray-50 opacity-60"
                  : selected.includes(t.id) ? "bg-violet-50"
                  : "hover:bg-slate-50"}`}>

                <td className="w-10 px-4 py-3 text-center">
                  <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleOne(t.id)}
                    disabled={isDone(t)}
                    className="cursor-pointer accent-violet-600 block mx-auto disabled:opacity-40 disabled:cursor-not-allowed" />
                </td>

                {/* Task Name */}
                <td className="px-4 py-3 font-medium">
                  {editId === t.id
                    ? <input autoFocus value={editTitle} onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                        className="border border-violet-400 rounded-lg px-2 py-1 text-sm text-slate-700 outline-none w-full shadow-[0_0_0_3px_rgba(124,58,237,0.1)]" />
                    : <span className={`text-slate-700 ${isDone(t) ? "line-through text-slate-400" : ""}`}>{t.title}</span>}
                </td>

                {/* Task Details */}
                <td className="px-4 py-3 text-xs text-slate-500 max-w-[220px]">
                  {editId === t.id
                    ? <textarea autoFocus value={editDetails} onChange={e => setEditDetails(e.target.value)}
                        onKeyDown={e => { if (e.key === "Escape") cancelEdit(); }}
                        rows={2}
                        placeholder="Add details..."
                        className="border border-violet-400 rounded-lg px-2 py-1 text-xs text-slate-700 outline-none w-full resize-none shadow-[0_0_0_3px_rgba(124,58,237,0.1)]" />
                    : t.details
                      ? <span className="line-clamp-2" title={t.details}>{t.details}</span>
                      : <span className="text-slate-300">—</span>}
                </td>

                {/* Functional Categories */}
                <td className="px-4 py-3 text-xs">
                  {editId === t.id
                    ? <div className="relative" ref={editDropRef}>
                        <button onClick={() => setEditDropOpen(o => !o)}
                          className="flex items-center gap-1.5 border border-violet-400 rounded-lg px-2 py-1 text-xs bg-white cursor-pointer outline-none w-full shadow-[0_0_0_3px_rgba(124,58,237,0.1)]">
                          <span className="flex-1 text-left flex flex-wrap gap-1">
                            {editGroup.length > 0
                              ? editGroup.map(g => { const o = funcCatOptions.find(x => x.label === g); return (
                                  <span key={g} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold"
                                    style={{ background: o?.bg, color: o?.color, border: `1px solid ${o?.border}` }}>
                                    {g}<span onClick={e => { e.stopPropagation(); setEditGroup(prev => prev.filter(x => x !== g)); }} className="cursor-pointer opacity-60 hover:opacity-100">×</span>
                                  </span>); })
                              : <span className="text-slate-400">Select...</span>}
                          </span>
                          <ChevronDown size={11} className={`text-slate-400 flex-shrink-0 ${editDropOpen ? "rotate-180" : "rotate-0"}`} />
                        </button>
                        {editDropOpen && (
                          <div className="absolute top-full mt-1 left-0 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-y-auto z-50 min-w-[220px] max-h-[220px]">
                            {funcCatOptions.map((o, i) => {
                              const checked = editGroup.includes(o.label);
                              return (
                                <div key={o.label}
                                  onClick={() => setEditGroup(prev => checked ? prev.filter(x => x !== o.label) : [...prev, o.label])}
                                  className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-xs ${i < funcCatOptions.length - 1 ? "border-b border-gray-50" : ""} ${checked ? "" : "hover:bg-slate-50"}`}
                                  style={checked ? { background: o.bg } : undefined}>
                                  <div className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center border-2 ${checked ? "border-violet-600 bg-violet-600" : "border-gray-300"}`}>
                                    {checked && <Check size={8} color="#fff" strokeWidth={3} />}
                                  </div>
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: o.color }} />
                                  <span style={{ color: checked ? o.color : "#374151" }}>{o.label}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    : <div className="flex flex-wrap gap-1">
                        {(t.group || []).map(g => { const o = funcCatOptions.find(x => x.label === g); return (
                          <span key={g} className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md"
                            style={{ background: o?.bg || "#f1f5f9", color: o?.color || "#64748b", border: `1px solid ${o?.border || "#e2e8f0"}` }}>
                            {g}
                          </span>
                        ); })}
                      </div>}
                </td>

                <td className="px-4 py-3 text-slate-500 text-xs">{t.created || "—"}</td>
                <td className="px-4 py-3">
                  <Badge label={t.status} colors={STATUS_COLORS[t.status] || STATUS_COLORS.open} />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {editId === t.id ? (
                      <>
                        <ActionBtn icon={<Check size={13}/>} label="Save"   color="#059669" bg="#ecfdf5" border="#a7f3d0" hoverBg="#d1fae5" onClick={saveEdit} />
                        <ActionBtn icon={<X size={13}/>}     label="Cancel" color="#64748b" bg="#f1f5f9" border="#e2e8f0" hoverBg="#e2e8f0" onClick={cancelEdit} />
                      </>
                    ) : isDone(t) ? (
                      <>
                        <ActionBtn icon={<RotateCcw size={13}/>} label="Undo Complete" color="#D97406" bg="#fffbeb" border="#fde68a" hoverBg="#fef3c7" onClick={() => handleUndo(t.id)} />
                        <ActionBtn icon={<Trash2 size={13}/>}    label="Delete"         color="#ef4444" bg="#fef2f2" border="#fecaca" hoverBg="#fee2e2" onClick={() => setDeleteId(t.id)} />
                      </>
                    ) : (
                      <>
                        <ActionBtn icon={<Pencil size={13}/>}       label="Edit"                 color="#7C3AED" bg="#f5f3ff" border="#ede9fe" hoverBg="#ede9fe" onClick={() => startEdit(t)} />
                        <ActionBtn icon={<Trash2 size={13}/>}       label="Delete"               color="#ef4444" bg="#fef2f2" border="#fecaca" hoverBg="#fee2e2" onClick={() => setDeleteId(t.id)} />
                        <ActionBtn icon={<ArrowUpRight size={13}/>} label="Convert to Full Task" color="#2563eb" bg="#eff6ff" border="#bfdbfe" hoverBg="#dbeafe" onClick={() => setConvertDraft({ todoId: t.id, taskName: t.title, categories: t.group || [], details: t.details || "" })} />
                        <ActionBtn icon={<CheckCheck size={13}/>}   label="Complete"             color="#059669" bg="#ecfdf5" border="#a7f3d0" hoverBg="#d1fae5" onClick={() => handleComplete(t.id)} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No todos found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}